import { Injectable } from '@angular/core';
import { HttpServiceBase } from '../core/http-service-base';
import { LoggerService } from './logger.service';
import { InjectorInstance } from '../core/injector-instance';
import { DQC_TIMEOUT } from '../core/globals';
import { environment } from '../../environments/environment';

export type RouteDecision =
  | {
      action: 'success';
      deviceScore?: number;
      deviceFailures?: string[];
      rawBody?: string;
    }
  | {
      action: 'blocked';
      deviceScore?: number;
      deviceFailures?: string[];
      rawBody?: string;
    };

export const DEMO_SURVEY_ID = 'ROUTER_DEMO';

@Injectable()
export class DqcService extends HttpServiceBase {
  private toolboxReady: Promise<any> | null = null;
  dqcResultPromise: Promise<void> | null = null;

  private _isDQCEnabled: boolean = false;

  get isDQCEnabled(): boolean {
    return environment.dqc.enabled && this._isDQCEnabled;
  }

  set isDQCEnabled(value: boolean) {
    this._isDQCEnabled = environment.dqc.enabled && value;
  }
  action: string = '';
  deviceScore: number | null = null;
  response: string = JSON.stringify({});

  constructor() {
    super();
  }

  private ensureToolbox(): Promise<any> {
    if (this.toolboxReady) {
      return this.toolboxReady;
    }

    const start = Date.now();
    const moduleLoaded: Promise<any> = (window as any).__dqcToolboxLoaded
      || Promise.reject(new Error('__dqcToolboxLoaded not found. Ensure index.html initializes toolbox loading promise.'));

    this.toolboxReady = this.withTimeout(moduleLoaded, 'DQC_EnsureToolbox', 8000)
      .then((toolboxModule: any) => {
        const moduleExports = toolboxModule && typeof toolboxModule === 'object' ? toolboxModule : {};

        if (!('DQCToolBox' in moduleExports)) {
          throw new Error(
            'DQC toolbox module has no DQCToolBox export. Exports: '
            + Object.keys(moduleExports).join(', ')
          );
        }

        const toolbox = moduleExports.DQCToolBox;

        if (!toolbox) {
          throw new Error(
            'DQC toolbox module exports DQCToolBox as '
            + (toolbox === null ? 'null' : typeof toolbox)
          );
        }

        if (typeof toolbox.getIdentityRequestId !== 'function') {
          throw new Error(
            'DQC toolbox exports DQCToolBox but getIdentityRequestId is '
            + typeof toolbox.getIdentityRequestId
          );
        }

        this.logTiming('DQC_EnsureToolbox', Date.now() - start, { status: 'ready' });

        return toolbox;
      })
      .catch((error) => {
        const isTimeout = error instanceof Error && error.message.indexOf('_TIMEOUT') !== -1;
        this.logTiming('DQC_EnsureToolbox', isTimeout ? 8000 : (Date.now() - start), { status: isTimeout ? 'timeout' : 'error' });
        this.toolboxReady = null;
        throw error;
      });

    return this.toolboxReady;
  }

  warmUpToolbox(): void {
    this.ensureToolbox().catch((error) => {
      console.error('Failed to warm up DQC toolbox', error);
    });
  }

  get timeoutMs(): number {
    return InjectorInstance.get<number>(DQC_TIMEOUT) * 1000;
  }

  private withTimeout<T>(promise: Promise<T>, label: string, overrideMs?: number): Promise<T> {
    const ms = overrideMs !== undefined ? overrideMs : this.timeoutMs;
    const start = Date.now();
    let timeoutId: any;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        const error: any = new Error(`${label}_TIMEOUT`);
        error.elapsedMs = Date.now() - start;
        reject(error);
      }, ms);
    });

    return Promise.race([promise, timeoutPromise]).then(
      (result) => {
        clearTimeout(timeoutId);
        return result as T;
      },
      (error) => {
        clearTimeout(timeoutId);
        throw error;
      }
    );
  }

  async getRequestId(surveyId: string): Promise<string | null> {
    try {
      const toolbox = await this.ensureToolbox();
      const result: any = await this.withTimeout(
        toolbox.getIdentityRequestId(surveyId),
        'DQC_GetRequestId'
      );
      return result && result.requestId ? result.requestId : null;
    } catch (error) {
      if (error instanceof Error && error.message.indexOf('_TIMEOUT') !== -1) {
        throw error;
      }
      console.error('Failed to get DQC requestId', error);
      return null;
    }
  }

  private logTiming(name: string, durationMs: number, properties?: { [name: string]: string }): void {
    try {
      const logger = InjectorInstance.get<LoggerService>(LoggerService);
      logger.trackMetric(name, durationMs, properties);
    } catch (e) {
      console.warn('Failed to log timing', e);
    }
  }

  routeDecision(
    input: { requestId: string; surveyId: string }
  ): Promise<RouteDecision> {
    return this.withTimeout(
      this.post<typeof input, RouteDecision>('/route', input),
      'DQC_RouteDecision'
    );
  }
}
